import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ImagePlus,
  Upload,
  Search,
  FileImage,
  FileAudio,
  RefreshCw,
  ExternalLink,
  X,
} from "lucide-react";

type MediaItem = {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  uploadedAt: string;
  owner: string;
  path: string;
  uploaderRoleType?: "user" | "admin" | null;
};

type MediaPickerProps = {
  onSelect: (url: string) => void;
  currentValue?: string;
  allowedTypes?: string[];
  title?: string;
  description?: string;
  buttonText?: string;
  showPreview?: boolean;
};

const MediaPicker = ({
  onSelect,
  currentValue = "",
  allowedTypes = ["image/jpeg", "image/jpg", "image/png"],
  title = "Select Media",
  description = "Choose an image from your media library or upload a new one",
  buttonText = "Select Image",
  showPreview = true,
}: MediaPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"library" | "upload" | "url">(
    "library"
  );
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(currentValue);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [customUrl, setCustomUrl] = useState<string>(currentValue);
  const [uploadProgress, setUploadProgress] = useState<{
    progress: number;
    isUploading: boolean;
  }>({ progress: 0, isUploading: false });

  const { user } = useAuth();
  const { toast } = useToast();

  // Update file size limits
  const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunk size

  // Add supported MIME types for audio files
  const SUPPORTED_AUDIO_TYPES = [
    "audio/mpeg",
    "audio/mp3",
    "audio/wav",
    "audio/ogg",
    "audio/x-m4a",
    "audio/aac",
  ];

  // Filter media items by allowed types, search query, and admin uploader only
  const filteredMediaItems = mediaItems.filter((item) => {
    const matchesType = allowedTypes.includes(item.type);
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    // Only show items uploaded by admins
    const matchesAdmin = item.uploaderRoleType === "admin";
    return matchesType && matchesSearch && matchesAdmin;
  });

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + " MB";
    else return (bytes / 1073741824).toFixed(1) + " GB";
  };

  // Fetch media items from the Supabase storage with simpler, more direct approach
  const fetchMediaItems = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log("Media picker: Fetching media items from storage...");

      // Get all files from the root level first
      const { data: rootLevelData, error: rootLevelError } =
        await supabase.storage.from("media").list("", { limit: 1000 });

      if (rootLevelError) {
        console.error("Error fetching root level:", rootLevelError);
        throw rootLevelError;
      }

      console.log("Root level items:", rootLevelData?.length || 0);

      // Initialize array to store all media items
      const allMediaItems: MediaItem[] = [];

      // Process each item at the root level
      if (rootLevelData) {
        for (const item of rootLevelData) {
          // Check if this is a "folder"
          if (item && !item.metadata) {
            // This is a folder, get its contents
            const { data: folderContents, error: folderError } =
              await supabase.storage
                .from("media")
                .list(item.name, { limit: 1000 });

            if (folderError) {
              console.error(`Error listing folder ${item.name}:`, folderError);
              continue;
            }

            // Process each file in the folder
            if (folderContents) {
              for (const file of folderContents) {
                if (file && file.metadata) {
                  // If it has metadata, it's a file not a subfolder
                  // Get the public URL for this file
                  const { data: urlData } = supabase.storage
                    .from("media")
                    .getPublicUrl(`${item.name}/${file.name}`);

                  allMediaItems.push({
                    id: file.id,
                    name: file.name,
                    type: file.metadata.mimetype || "",
                    url: urlData.publicUrl,
                    size: file.metadata.size || 0,
                    uploadedAt: file.created_at || "",
                    owner: item.name,
                    path: `${item.name}/${file.name}`,
                  });
                }
              }
            }
          } else if (item && item.metadata) {
            // This is a file at root level
            const { data: urlData } = supabase.storage
              .from("media")
              .getPublicUrl(item.name);

            allMediaItems.push({
              id: item.id,
              name: item.name,
              type: item.metadata.mimetype || "",
              url: urlData.publicUrl,
              size: item.metadata.size || 0,
              uploadedAt: item.created_at || "",
              owner: "root",
              path: item.name,
            });
          }
        }
      }

      // After processing all media items, sort them by upload date (most recent first)
      console.log(`Media picker found ${allMediaItems.length} media items`);

      // Sort media items by upload date (most recent first)
      allMediaItems.sort((a, b) => {
        // Convert strings to Date objects
        const dateA = new Date(a.uploadedAt);
        const dateB = new Date(b.uploadedAt);

        // Sort in descending order (newest first)
        return dateB.getTime() - dateA.getTime();
      });

      // Fetch user role information to determine uploader type
      try {
        // Get unique owner UIDs (excluding "root")
        const uniqueOwnerUids = [
          ...new Set(
            allMediaItems
              .map((item) => item.owner)
              .filter((owner) => owner !== "root")
          ),
        ];

        if (uniqueOwnerUids.length > 0) {
          // Query profiles to get profile IDs for each owner UID
          const { data: profiles, error: profilesError } = await supabase
            .from("profiles")
            .select("id, uid")
            .in("uid", uniqueOwnerUids);

          if (profilesError) {
            console.error("Error fetching profiles:", profilesError);
          } else if (profiles && profiles.length > 0) {
            // Create a map of UID to profile ID
            const uidToProfileIdMap = new Map(
              profiles.map((p) => [p.uid, p.id])
            );

            // Get all profile IDs
            const profileIds = profiles.map((p) => p.id);

            // Query user_to_roles to get roles for each profile
            const { data: userRoles, error: userRolesError } = await supabase
              .from("user_to_roles")
              .select("user_id, role_id")
              .in("user_id", profileIds);

            if (userRolesError) {
              console.error("Error fetching user roles:", userRolesError);
            } else if (userRoles) {
              // Group roles by user_id
              const userRolesMap = new Map<number, number[]>();
              userRoles.forEach((ur) => {
                const existing = userRolesMap.get(ur.user_id) || [];
                existing.push(ur.role_id);
                userRolesMap.set(ur.user_id, existing);
              });

              // Create a map of UID to role type
              const uidToRoleTypeMap = new Map<string, "user" | "admin">();
              uidToProfileIdMap.forEach((profileId, uid) => {
                const roleIds = userRolesMap.get(profileId) || [];
                // If user has ONLY role 5, they're a "user", otherwise "admin"
                const isUserOnly =
                  roleIds.length === 1 && roleIds[0] === 5;
                uidToRoleTypeMap.set(uid, isUserOnly ? "user" : "admin");
              });

              // Map role type back to media items
              allMediaItems.forEach((item) => {
                if (item.owner !== "root") {
                  item.uploaderRoleType =
                    uidToRoleTypeMap.get(item.owner) || null;
                } else {
                  item.uploaderRoleType = null;
                }
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user role information:", error);
        // Continue even if role fetching fails - items will just have null uploaderRoleType
      }

      setMediaItems(allMediaItems);

      // If there's a current value, try to find and preselect it
      if (currentValue) {
        const matchingItem = allMediaItems.find(
          (item) => item.url === currentValue
        );
        if (matchingItem) {
          setSelectedItem(matchingItem.id);
        }
      }
    } catch (error) {
      console.error("Error fetching media items:", error);
      toast({
        title: "Error",
        description: "Failed to load media items",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentValue, toast]);

  // Generate a safe filename to avoid collisions
  const generateSafeFilename = (filename: string): string => {
    // Decode URI components to handle special characters
    const decodedFilename = decodeURIComponent(filename);

    // Extract the file extension
    const lastDotIndex = decodedFilename.lastIndexOf(".");
    const ext =
      lastDotIndex !== -1 ? decodedFilename.substring(lastDotIndex) : "";
    const nameWithoutExt =
      lastDotIndex !== -1
        ? decodedFilename.substring(0, lastDotIndex)
        : decodedFilename;

    // Clean the filename: replace spaces with hyphens and remove special characters
    const cleanName = nameWithoutExt
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .trim() // Remove leading/trailing spaces
      .replace(/\s+/g, "-"); // Replace spaces with hyphens

    // Add a short timestamp (last 4 digits) to ensure uniqueness
    const timestamp = new Date().getTime().toString().slice(-4);
    return `${cleanName}-${timestamp}${ext}`;
  };

  // Function to split file into chunks
  const splitFileIntoChunks = (file: File, chunkSize: number) => {
    const chunks = [];
    let start = 0;

    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      chunks.push(chunk);
      start = end;
    }

    return chunks;
  };

  // Update handleFileUpload with separate size limits
  const handleFileUpload = async () => {
    if (!uploadFile || !user) return;

    const isAudioFile = uploadFile.type.startsWith("audio/");
    const maxSize = isAudioFile ? MAX_AUDIO_SIZE : MAX_IMAGE_SIZE;

    // Check file size
    if (uploadFile.size > maxSize) {
      toast({
        title: "File too large",
        description: `File size must be less than ${formatFileSize(maxSize)}`,
        variant: "destructive",
      });
      return;
    }

    // Validate MIME type for audio files
    if (
      allowedTypes.includes("audio/mpeg") &&
      !SUPPORTED_AUDIO_TYPES.includes(uploadFile.type)
    ) {
      toast({
        title: "Unsupported file type",
        description:
          "Please upload a supported audio file format (MP3, WAV, OGG, M4A, or AAC)",
        variant: "destructive",
      });
      return;
    }

    setUploadProgress({ progress: 0, isUploading: true });

    try {
      // Generate a safe filename
      const safeFileName = generateSafeFilename(uploadFile.name);
      const filePath = `${user.uid}/${safeFileName}`;

      // For audio files, upload directly without chunking
      const { error: uploadError } = await supabase.storage
        .from("media")
        .upload(filePath, uploadFile, {
          cacheControl: "3600",
          upsert: false,
          contentType: uploadFile.type, // Explicitly set the content type
        });

      if (uploadError) throw uploadError;

      // Get the public URL of the uploaded file
      const { data: urlData } = supabase.storage
        .from("media")
        .getPublicUrl(filePath);

      toast({
        title: "Upload Successful",
        description: `File uploaded successfully`,
      });

      // Refresh the media items list
      await fetchMediaItems();

      // Return the URL of the uploaded file
      onSelect(urlData.publicUrl);
      setPreviewUrl(urlData.publicUrl);
      setIsOpen(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload Error",
        description:
          error instanceof Error ? error.message : "Failed to upload file",
        variant: "destructive",
      });
    } finally {
      setUploadProgress({ progress: 0, isUploading: false });
    }
  };

  // Handle selection from media library
  const handleSelectMedia = () => {
    if (!selectedItem) {
      toast({
        title: "Selection Required",
        description: "Please select a media item first",
        variant: "destructive",
      });
      return;
    }

    const selected = mediaItems.find((item) => item.id === selectedItem);

    if (selected) {
      onSelect(selected.url);
      setPreviewUrl(selected.url);
      setIsOpen(false);
    }
  };

  // Handle selection from URL input
  const handleSelectUrl = () => {
    if (!customUrl || !customUrl.trim()) {
      toast({
        title: "URL Required",
        description: "Please enter a valid URL",
        variant: "destructive",
      });
      return;
    }

    onSelect(customUrl);
    setPreviewUrl(customUrl);
    setIsOpen(false);
  };

  // Handle submit based on active tab
  const handleSubmit = () => {
    if (activeTab === "library") {
      handleSelectMedia();
    } else if (activeTab === "upload" && uploadFile) {
      handleFileUpload();
    } else if (activeTab === "url") {
      handleSelectUrl();
    }
  };

  // Set preview URL when currentValue changes
  useEffect(() => {
    setPreviewUrl(currentValue || "");
    setCustomUrl(currentValue || "");

    // If dialog is opened, also try to select the item in the library
    if (isOpen && currentValue) {
      const matchingItem = mediaItems.find((item) => item.url === currentValue);
      if (matchingItem) {
        setSelectedItem(matchingItem.id);
      }
    }
  }, [currentValue, isOpen, mediaItems]);

  // Fetch media items when dialog is opened
  useEffect(() => {
    if (isOpen) {
      fetchMediaItems();
    }
  }, [isOpen, fetchMediaItems]);

  // Update preview when file is selected for upload
  useEffect(() => {
    if (uploadFile) {
      const url = URL.createObjectURL(uploadFile);
      setPreviewUrl(url);

      // Cleanup function to revoke the object URL when component unmounts
      return () => URL.revokeObjectURL(url);
    }
  }, [uploadFile]);

  return (
    <div className="space-y-4">
      {/* Preview of the selected media - only show if showPreview is true */}
      {showPreview && (
        <div className="relative">
          <div className="border rounded-md flex items-center justify-center bg-muted/30 h-[200px] overflow-hidden">
            {previewUrl ? (
              allowedTypes.some((type) => type.startsWith("image/")) ? (
                <img
                  src={previewUrl}
                  alt="Selected media"
                  className="max-h-full max-w-full object-contain"
                  onError={() => {
                    setPreviewUrl("");
                    toast({
                      title: "Image Error",
                      description: "Failed to load image from URL",
                      variant: "destructive",
                    });
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <FileAudio className="h-16 w-16 text-blue-500" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    Audio file selected
                  </p>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center">
                <FileImage className="h-16 w-16 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  No media selected
                </p>
              </div>
            )}

            {previewUrl && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 bg-background rounded-full hover:bg-destructive hover:text-white transition-colors"
                onClick={() => {
                  setPreviewUrl("");
                  onSelect("");
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {previewUrl && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {previewUrl.startsWith("blob:")
                ? "New upload (not saved yet)"
                : previewUrl}
            </p>
          )}
        </div>
      )}

      {/* Button to open the media picker dialog */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(true)}
          className="w-full"
        >
          <ImagePlus className="mr-2 h-4 w-4" />
          {previewUrl && !showPreview ? "Change " : ""}
          {buttonText}
        </Button>

        {/* Show remove button only when file is selected but preview is hidden */}
        {previewUrl && !showPreview && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="flex-shrink-0 text-destructive hover:bg-destructive hover:text-white"
            onClick={() => {
              setPreviewUrl("");
              onSelect("");
              toast({
                title: "Media removed",
                description: "The selected media has been removed",
              });
            }}
            title="Remove selected media"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Show filename when preview is hidden but file is selected */}
      {previewUrl && !showPreview && (
        <p className="text-xs text-muted-foreground mt-1 truncate">
          Selected:{" "}
          {previewUrl.startsWith("blob:")
            ? "New upload (not saved yet)"
            : previewUrl.split("/").pop() || previewUrl}
        </p>
      )}

      {/* Media picker dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <Tabs
            defaultValue="library"
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "library" | "upload" | "url")
            }
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="library">Media Library</TabsTrigger>
              <TabsTrigger value="upload">Upload</TabsTrigger>
              <TabsTrigger value="url">External URL</TabsTrigger>
            </TabsList>

            {/* Media Library Tab */}
            <TabsContent value="library" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search files..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchMediaItems}
                  disabled={isLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>

              {isLoading ? (
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div
                      key={index}
                      className="h-[120px] animate-pulse bg-muted rounded"
                    ></div>
                  ))}
                </div>
              ) : filteredMediaItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="rounded-full bg-muted p-3">
                    <FileImage className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="mt-4 text-md font-medium">
                    No matching media
                  </h3>
                  <p className="mt-2 text-sm text-center text-muted-foreground">
                    {mediaItems.length === 0
                      ? "Your media library is empty. Try uploading some files first."
                      : "No files match your filter criteria. Try a different search or upload a new file."}
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="grid grid-cols-3 gap-4">
                    {filteredMediaItems.map((item) => (
                      <div
                        key={item.id}
                        className={`relative border rounded-md overflow-hidden cursor-pointer transition-all ${
                          selectedItem === item.id
                            ? "ring-2 ring-primary"
                            : "hover:ring-1 hover:ring-primary/50"
                        }`}
                        onClick={() => setSelectedItem(item.id)}
                      >
                        <div className="relative h-[120px]">
                          {/* Selection checkbox */}
                          <div className="absolute top-2 right-2 z-10">
                            <Checkbox
                              checked={selectedItem === item.id}
                              className="h-5 w-5 bg-white/90 border-gray-300 rounded-sm"
                            />
                          </div>

                          {item.type.startsWith("image/") ? (
                            <div
                              className="absolute inset-0 bg-center bg-cover bg-no-repeat"
                              style={{
                                backgroundImage: `url(${item.url})`,
                              }}
                            ></div>
                          ) : item.type.startsWith("audio/") ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
                              <FileAudio className="h-16 w-16 text-blue-500" />
                            </div>
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-muted">
                              <FileImage className="h-16 w-16 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        <div className="p-2">
                          <p className="text-sm font-medium truncate">
                            {item.name}
                          </p>
                          <div className="flex justify-between items-center mt-1">
                            <Badge variant="outline" className="text-xs">
                              {formatFileSize(item.size)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(item.uploadedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-4">
              {uploadProgress.isUploading ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-sm font-medium">Uploading file...</div>
                    <div className="text-sm text-muted-foreground">
                      {uploadProgress.progress}%
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2.5">
                    <div
                      className="bg-primary h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.progress}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-center mt-4">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="media-upload">
                      Upload{" "}
                      {allowedTypes.includes("audio/mpeg") ? "Audio" : "Image"}
                    </Label>
                    <div
                      className="border-2 border-dashed rounded-lg p-12 text-center hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() =>
                        document.getElementById("media-upload")?.click()
                      }
                    >
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                      <p className="mt-2 text-sm font-medium">
                        Drag and drop a file here or click to select
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {allowedTypes.includes("audio/mpeg")
                          ? `Support: MP3, WAV, OGG, M4A, AAC (up to ${formatFileSize(
                              MAX_AUDIO_SIZE
                            )})`
                          : allowedTypes.includes("image/jpeg")
                          ? `Support: JPG, PNG (up to ${formatFileSize(
                              MAX_IMAGE_SIZE
                            )})`
                          : `Select a file up to ${formatFileSize(
                              MAX_IMAGE_SIZE
                            )}`}
                      </p>
                      <Input
                        id="media-upload"
                        type="file"
                        className="hidden"
                        accept={allowedTypes.join(",")}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const isAudioFile = file.type.startsWith("audio/");
                            const maxSize = isAudioFile
                              ? MAX_AUDIO_SIZE
                              : MAX_IMAGE_SIZE;
                            if (file.size > maxSize) {
                              toast({
                                title: "File too large",
                                description: `File size must be less than ${formatFileSize(
                                  maxSize
                                )}`,
                                variant: "destructive",
                              });
                              e.target.value = ""; // Reset the input
                              return;
                            }
                            setUploadFile(file);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {uploadFile && (
                    <div className="mt-4 p-4 border rounded-md">
                      <div className="flex items-center">
                        {uploadFile.type.startsWith("image/") ? (
                          <div className="h-16 w-16 rounded overflow-hidden mr-4">
                            <img
                              src={URL.createObjectURL(uploadFile)}
                              alt="Preview"
                              className="h-full w-full object-cover"
                            />
                          </div>
                        ) : uploadFile.type.startsWith("audio/") ? (
                          <div className="h-16 w-16 bg-blue-50 dark:bg-blue-900/30 rounded flex items-center justify-center mr-4">
                            <FileAudio className="h-8 w-8 text-blue-500" />
                          </div>
                        ) : (
                          <div className="h-16 w-16 bg-muted rounded flex items-center justify-center mr-4">
                            <FileImage className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {uploadFile.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatFileSize(uploadFile.size)} •{" "}
                            {uploadFile.type}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUploadFile(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* External URL Tab */}
            <TabsContent value="url" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="external-url">Image URL</Label>
                  <Input
                    id="external-url"
                    placeholder="https://example.com/image.jpg"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Enter the URL of an image from another website. Make sure
                    you have permission to use this image.
                  </p>
                </div>

                {customUrl && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="relative h-[200px] bg-muted flex items-center justify-center">
                      {allowedTypes.some((type) =>
                        type.startsWith("image/")
                      ) ? (
                        <img
                          src={customUrl}
                          alt="URL preview"
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect width="18" height="18" x="3" y="3" rx="2" ry="2"/%3E%3Ccircle cx="9" cy="9" r="2"/%3E%3Cpath d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <FileAudio className="h-16 w-16 text-blue-500" />
                      )}
                    </div>
                    <div className="p-3 bg-muted/30 border-t flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileImage className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground truncate max-w-[400px]">
                          {customUrl.split("/").pop() || customUrl}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(customUrl, "_blank")}
                        className="ml-2 flex-shrink-0"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                (activeTab === "library" && !selectedItem) ||
                (activeTab === "upload" &&
                  !uploadFile &&
                  !uploadProgress.isUploading) ||
                (activeTab === "url" && !customUrl) ||
                uploadProgress.isUploading
              }
            >
              {activeTab === "upload" && uploadProgress.isUploading
                ? "Uploading..."
                : "Select"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaPicker;
